import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { fetchProjectLookup } from '../lib/entity-api';
import { useUser } from '../contexts/UserContext';
import { GenericSearchableSelect } from '../styles/GenericSearchableSelect';
import { readStoredProjectSelection, writeStoredProjectSelection } from '../lib/selected-project-storage';

interface Project {
  id: string;
  name: string;
  status: string;
  company_id: string;
}

interface ProjectContextType {
  selectedProject: string;
  setSelectedProject: (id: string) => void;
  projects: Project[];
}

const ProjectContext = createContext<ProjectContextType>({
  selectedProject: 'all',
  setSelectedProject: () => {},
  projects: [],
});

export const useProject = () => useContext(ProjectContext);

/**
 * The single source for "what Project should a brand-new record's Project
 * dropdown start on" -- every "New X" modal with a Project field should
 * seed its initial project_id with this, instead of each modal
 * reimplementing the same `selectedProject === 'all' ? '' : selectedProject`
 * check. Returns '' (renders as "Select") when the global filter is on
 * "All Projects", since there's no single project to default to.
 */
export function getDefaultProjectIdForNewRecord(selectedProject: string): string {
  return selectedProject && selectedProject !== 'all' ? selectedProject : '';
}

/**
 * The single width rule for a standalone Project dropdown placed inline
 * next to a label (a toolbar-style selector, not one inside a form grid
 * where the shared `w-full` already stretches it to its column) -- e.g.
 * Budgets' and Time Tracking's own "Project:" selector above their list.
 * Sized off the viewport, not the selected option's text, with a floor
 * and ceiling so it never gets uncomfortably narrow or unreasonably wide.
 * Reuse this instead of a one-off `w-[Nrem]` per screen.
 */
export const PROJECT_SELECT_WIDTH_CLASS = 'w-[clamp(12.5rem,20vw,20rem)]';

export function ProjectProvider({ children }: { children: React.ReactNode }) {
  const [projects, setProjects] = useState<Project[]>([]);
  // The restored choice is read synchronously so the very first render (and every screen's first
  // query) already uses it -- no flash of "All Projects" followed by a switch. It is checked below
  // against who is logged in and which projects they can actually see.
  const [selectedProject, setSelectedProject] = useState<string>(() => readStoredProjectSelection()?.projectId ?? 'all');
  const { companyId, isLoading, user } = useUser();
  const userId = user?.id;
  const checkedIdentity = useRef<string | null>(null);

  // Persist the choice (and discard one that belongs to another user / company).
  useEffect(() => {
    if (isLoading || !userId || !companyId) return;
    const identity = `${userId}:${companyId}`;
    if (checkedIdentity.current !== identity) {
      checkedIdentity.current = identity;
      const stored = readStoredProjectSelection();
      if (stored && (stored.userId !== userId || stored.companyId !== companyId)) {
        setSelectedProject('all');
        writeStoredProjectSelection({ userId, companyId, projectId: 'all' });
        return;
      }
    }
    writeStoredProjectSelection({ userId, companyId, projectId: selectedProject });
  }, [selectedProject, userId, companyId, isLoading]);

  useEffect(() => {
    // Always render children, don't block the app
    // If we have companyId, fetch projects
    if (companyId && !isLoading) {
      fetchProjects();
    }
  }, [companyId, isLoading]);

  const fetchProjects = async () => {
    try {
      if (!companyId) return;

      // The company's active projects (generic lookup, by the API), by name
      const data: Project[] = (await fetchProjectLookup()).map((project) => ({ id: project.id, name: project.name, status: project.status ?? 'active', company_id: companyId }));

      setProjects(data || []);
      // A restored project that no longer exists / is no longer accessible falls back to a valid state.
      setSelectedProject((current) => (current !== 'all' && !(data || []).some((p) => p.id === current) ? 'all' : current));
    } catch (error) {
      console.error('❌ Exception fetching projects:', error);
    }
  };

  return (
    <ProjectContext.Provider value={{ selectedProject, setSelectedProject, projects }}>
      {children}
    </ProjectContext.Provider>
  );
}

export function ProjectSelector() {
  const { selectedProject, setSelectedProject, projects } = useProject();
  const { companyId, isLoading } = useUser();

  const handleProjectChange = (value: string) => {
    setSelectedProject(value);
  };

  // Don't block the app while UserContext is loading
  if (isLoading) {
    return (
      // unmapped design value: w-[200px] repeated 2x in this file (loading/setup placeholders only -- the real selector below now uses triggerStyle instead), no shared token exists for it
      <div className="w-[200px] h-10 bg-gray-100 animate-pulse rounded-md flex items-center justify-center">
        <span className="text-gray-500">Loading...</span>
      </div>
    );
  }

  // Show message if no company relation found, but don't block the app
  if (!companyId) {
    return (
      <div className="w-[200px] h-10 bg-yellow-50 border border-yellow-200 rounded-md flex items-center justify-center">
        <span className="text-yellow-700 text-body">Setting up...</span>
      </div>
    );
  }

  // You can type to find a project (the list can be long): the generic searchable select filters by name as you type, shows "No projects match" when nothing does, and
  // offers nothing else - never a "create project". "All Projects" is one more option, so choosing it clears the filter. The bounds are the header slot's.
  return (
    <div style={{ width: 'clamp(15rem, 24vw, 27rem)', maxWidth: '100%' }}>
      <GenericSearchableSelect
        ariaLabel="Project"
        value={selectedProject}
        onValueChange={handleProjectChange}
        className="form-text truncate"
        placeholder="Select"
        emptyText="No projects match"
        options={[
          { value: 'all', label: 'All Projects' },
          ...projects.map((project) => ({ value: project.id, label: project.name })),
        ]}
      />
    </div>
  );
}
